/** @jsxImportSource @emotion/react */

import { useContext, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import AuthContext from "../../context/auth-context";
import Client from "client";
import { useMutation } from "hooks";

import Icon from "components/icon/icon";
import { FormElement } from "components/form-element/form-element";
import Button from "components/button/button";
import Logo from "components/logo/logo";

import { appColors } from "emotion/utils";
import css from "./login.module.css";

const validate = () => ({});

function ResetPassword(props) {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [special, setSpecial] = useState(false);
  const [lengthOk, setLengthOk] = useState(false);
  const [match, setMatch] = useState(false);

  const specialReg =
    /^[0-9A-Za-z]*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?][0-9a-zA-Z]*$/;
  let uid = searchParams.get("userId");
  let token = searchParams.get("token");
  // console.log(uid);

  const initialValues = {
    confirmPassword: "",
    password: "",
    userId: uid,
    token,
  };
  const toastRef = useRef();
  const [hasError, setHasError] = useState(false);

  const { login } = useContext(AuthContext);

  const { mutateAsync: onSubmit, isLoading } = useMutation({
    mutationKey: "reset_password",
    mutationFn: async (data) => Client.post("/auth/reset-password", data),
    onSuccess: (data) => {
      alert("Congratulations, your password has been changed!");
      login(data);
      navigate("/login", { replace: true });
    },
    onError: (error) => {
      toastRef.current.show({
        severity: "error",
        summary: "Error",
        detail: error.toString(),
        life: 13000,
      });
      setHasError(true);
    },
  });

  const header = <Logo />;

  const check = (pass, confirmpass) => {
    setLengthOk(pass && pass.length >= 8);
    setMatch(pass === confirmpass && pass && pass.length > 0);
    setSpecial(pass.match(specialReg));
  };

  const getOkBox = (
    <div className={css.okBox}>
      {[
        { label: "Must be at least 8 characters", value: lengthOk },
        { label: "Must contain one special character", value: special },
        { label: "Passwords Match", value: match },
      ].map((item, index) => (
        <div className={css.box} key={index}>
          <input type="checkbox" checked={item.value} />
          <Icon
            icon="Check"
            fill={
              item.value ? appColors?.greens?.[700] : appColors?.blues?.blueGray
            }
            stroke={appColors?.grays?.[0]}
          />
          <label>{item.label}</label>
        </div>
      ))}
    </div>
  );

  const form = (formik) => (
    <div className={css.formBlock}>
      <div className={css.intro}>
        <h1>Set new password</h1>
        <p>Let's get you back in the saddle.</p>
      </div>

      <div className={css.elements}>
        <FormElement
          element="Password"
          elementType="password"
          id="password"
          name="password"
          formik={formik}
          onChange={(evt) => {
            check(evt.target.value, formik.values["confirmPassword"]);
            formik.handleChange(evt);
          }}
          label="Password Reset"
        />
        {formik.touched.password && formik.errors.password && (
          <span>{formik.errors.password}</span>
        )}

        <FormElement
          element="Password"
          elementType="password"
          id="confirmPassword"
          name="confirmPassword"
          formik={formik}
          onChange={(evt) => {
            check(formik.values["password"], evt.target.value);
            formik.handleChange(evt);
          }}
          label="Confirm Password"
        />
        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
          <span>{formik.errors.confirmPassword}</span>
        )}
      </div>

      {getOkBox}

      <Button
        label="Reset Password"
        loading={isLoading}
        disabled={
          isLoading ||
          Object.values(formik.errors).length ||
          hasError ||
          !formik.dirty
        }
        type="submit"
      />
    </div>
  );

  return (
    <div className={css.container}>
      <div className={css.block}>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={Yup.object({
            password: Yup.string()
              .label("Password")
              .min(8)
              .required()
              .matches(specialReg, "Need one special character"),
            confirmPassword: Yup.string()
              .label("Confirm Password")
              .required()
              .oneOf([Yup.ref("password")], "Passwords must match"),
          })}
        >
          {(formik) => (
            <Form>
              {header}
              {form(formik)}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default ResetPassword;
